/* eslint-disable camelcase */
import React, { useState, useEffect, useContext } from 'react';
import {
  Alert, Box, Button, Container, Divider, MenuItem, Snackbar, TextField,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ROUTES from '../../../../Configs/routes';
import Elements from '../../../Elements';
import { sellMultiItemToMarket } from '../../../../Redux/Models/market';
import { getUserData } from '../../../../Utils/storages';
import AlertContext from '../../../../Context/AlertContext';

export default function SellProduct({
  classes, error, message, isLoading, forSale,
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const locationSent = useLocation();
  const { t } = useTranslation();

  const user = getUserData();
  const { openAlert } = useContext(AlertContext);

  const [selectedItem, setSelectedItem] = useState([]);

  const [openPop, setOpenPop] = useState(false);

  const imageUrl = (i) => i.item?.photo ?? i.box?.photo;

  const handleChangeSaleField = (e, id) => {
    setSelectedItem(selectedItem.map((item) => {
      if (item.id === id) {
        return ({
          ...item,
          [e.target.name]: e.target.value,
        });
      }

      return item;
    }));
  };

  useEffect(() => {
    if (locationSent.state?.length) {
      setSelectedItem(locationSent.state.map((item) => ({
        ...item,
        sale_type: '-',
        starting_price: '',
        bid_duration: '-',
        description: '',
      })));
    } else {
      navigate(ROUTES.market());
    }
  }, [locationSent.state]);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const closeSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  useEffect(() => {
    if (error) {
      setSnackbar({
        open: true,
        message: `Something went wrong. ${message}`,
        severity: 'error',
      });
    }
  }, [error]);

  const wantToSell = () => {
    if (selectedItem.every((item) => {
      const hasPrice = Number(item.starting_price) > 0;
      return (item.sale_type === 'Fixed Price' && hasPrice && item.description)
        || (item.sale_type === 'Bid Price' && hasPrice && item.description && item.bid_duration);
    })) {
      setOpenPop(true);
    } else {
      setSnackbar({
        open: true,
        message: 'Please complete the form!',
        severity: 'error',
      });
    }
  };

  const sellItem = () => {
    const box = selectedItem.filter((i) => i.type === 'Box').map((i) => {
      if (i.sale_type === 'Bid Price') {
        const end_date = new Date();
        end_date.setDate(end_date.getDate() + Number(i.bid_duration));
        return ({
          ...i,
          end_date,
          starting_price: Number(i?.starting_price),
        });
      }
      return { ...i, starting_price: Number(i?.starting_price) };
    });
    const item = selectedItem.filter((i) => i.type === 'Item').map((i) => {
      if (i.sale_type === 'Bid Price') {
        const end_date = new Date();
        end_date.setDate(end_date.getDate() + Number(i.bid_duration));
        return ({
          ...i,
          end_date,
          starting_price: Number(i?.starting_price),
        });
      }
      return { ...i, starting_price: Number(i?.starting_price) };
    });

    const body = {
      item,
      box,
    };

    dispatch(sellMultiItemToMarket(user.id, body));
  };

  useEffect(() => {
    if (forSale) {
      openAlert({
        message: 'Successfully sell items/boxes to market',
        severity: 'success',
      });
      navigate(-1);
    }
  }, [forSale]);

  return (
    <Elements.PageBase navigate={navigate} noNav>
      <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={snackbar.open} autoHideDuration={10000} onClose={closeSnackbar}>
        <Alert onClose={closeSnackbar} severity={snackbar.severity || 'success'}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      <div className={classes.root}>
        <Elements.Navigator title={t('sale.title')} />

        <Container sx={{ mt: 3 }}>
          {selectedItem.map((item) => (
            <Box key={item.id} pb={2}>
              <h4>{t('sale.selected')}</h4>
              <Box className="selected-item rounded flex gap-8 mt-3">
                <img
                  src={imageUrl(item)}
                  alt="item"
                  width="100"
                  height="100"
                  className="rounded contain"
                  onClick={() => window.open(imageUrl(item), '_blank')}
                />
                <div>
                  <div className="s-14 two-line weight-600">{item.name}</div>
                  <div className="s-15 mt-2">
                    {t('sale.type')}
                    :
                    {' '}
                    {item.type || '-'}
                  </div>
                  <div className="s-15 mt-2">
                    {t('sale.condition')}
                    :
                    {' '}
                    {item.condition?.name || '-'}
                  </div>
                  <div className="s-15 mt-1">
                    {t('sale.category')}
                    :
                    {' '}
                    {item.category?.name || '-'}
                  </div>
                </div>
              </Box>

              <h4 className="mt-5">{t('sale.market')}</h4>
              <div className="mt-3">
                <label>{t('sale.field1')}</label>
                <TextField fullWidth select value={item.sale_type} name="sale_type" onChange={(e) => handleChangeSaleField(e, item.id)}>
                  <MenuItem value="-" disabled>{t('sale.chooseType')}</MenuItem>
                  <MenuItem value="Fixed Price">{t('sale.fixed')}</MenuItem>
                  <MenuItem value="Bid Price">{t('sale.bid')}</MenuItem>
                </TextField>
              </div>

              <div className="mt-3" hidden={item.sale_type !== 'Fixed Price'}>
                <label>{t('sale.field2')}</label>
                <TextField
                  fullWidth
                  placeholder="100"
                  InputProps={{
                    startAdornment: (<strong className="s-16">₣</strong>),
                  }}
                  value={item.starting_price}
                  name="starting_price"
                  onChange={(e) => handleChangeSaleField(e, item.id)}
                />
              </div>

              <div className="mt-3" hidden={item.sale_type !== 'Bid Price'}>
                <label>{t('sale.field3')}</label>
                <TextField
                  fullWidth
                  placeholder="100"
                  InputProps={{
                    startAdornment: (<strong className="s-16">₣</strong>),
                  }}
                  value={item.starting_price}
                  name="starting_price"
                  onChange={(e) => handleChangeSaleField(e, item.id)}
                />
              </div>

              <div className="mt-3" hidden={item.sale_type !== 'Bid Price'}>
                <label>{t('sale.field4')}</label>
                <TextField fullWidth select value={item.bid_duration} name="bid_duration" onChange={(e) => handleChangeSaleField(e, item.id)}>
                  <MenuItem value="-" disabled>{t('sale.chooseDuration')}</MenuItem>
                  <MenuItem value={3}>3 days</MenuItem>
                  <MenuItem value={7}>7 days</MenuItem>
                  <MenuItem value={14}>2 weeks</MenuItem>
                  <MenuItem value={21}>3 weeks</MenuItem>
                  <MenuItem value={30}>1 month</MenuItem>
                </TextField>
              </div>

              <div className="mt-3">
                <label>{t('sale.field5')}</label>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  maxRows={12}
                  value={item.description}
                  name="description"
                  onChange={(e) => handleChangeSaleField(e, item.id)}
                />
              </div>
              <Divider sx={{ my: 3 }} />
            </Box>
          ))}
        </Container>

        <div>
          <Container>
            <Button fullWidth size="large" variant="contained" className="btn-contained" onClick={wantToSell}>
              {t('sale.sell')}
            </Button>
          </Container>
        </div>
      </div>

      <Elements.ConfirmationModal
        open={openPop}
        desc={(
          <div>
            {t('sale.confirm')}
          </div>
        )}
        handleClose={() => setOpenPop(false)}
        handleAgree={sellItem}
        confirmText="Sell"
        isLoading={isLoading}
      />
    </Elements.PageBase>
  );
}
